import { getDbSupabase } from './supabase';
import { parseDeliveryDateToLocalDate } from './demDeliveryDate';
import { formatSupabaseError } from './formatSupabaseError';
import type { DemCustomer, DemPoExtractHeader, DemPoReviewRow } from '../types/dem';

export type DemPriceRow = {
  item: string;
  type: string;
  code: string;
  start: Date;
  end: Date;
  uom: string;
  min_qty: number;
  price: number;
};

function normalizeUpper(value: string): string {
  return value.trim().toUpperCase();
}

function cleanPriceType(value: string): string {
  return String(value ?? '')
    .toLowerCase()
    .replace('.0', '')
    .trim();
}

function cleanPriceCode(value: string): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace('.0', '');
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDbDate(raw: unknown, useMaxWhenEmpty = false): Date {
  if (!raw) return useMaxWhenEmpty ? new Date(9999, 11, 31) : new Date(0);
  const parsed = new Date(String(raw));
  if (Number.isNaN(parsed.getTime())) {
    return useMaxWhenEmpty ? new Date(9999, 11, 31) : new Date(0);
  }
  return startOfDay(parsed);
}

function minPrice(values: number[]): number | null {
  if (values.length === 0) return null;
  return Math.min(...values);
}

function customerPriceCodeVariants(code: string): string[] {
  const clean = cleanPriceCode(code);
  const variants = new Set([clean]);
  if (clean.startsWith('I') && clean.length > 1) variants.add('1' + clean.slice(1));
  if (clean.startsWith('1') && clean.length > 1) variants.add('I' + clean.slice(1));
  const outletS = /^([A-Z0-9]+)-S(\d+)$/.exec(clean);
  if (outletS) variants.add(`${outletS[1]}-5${outletS[2]}`);
  const outlet5 = /^([A-Z0-9]+)-5(\d+)$/.exec(clean);
  if (outlet5) variants.add(`${outlet5[1]}-S${outlet5[2]}`);
  return [...variants];
}

function isSiblingOutletPriceCode(mainCustCode: string, custCode: string, priceCode: string): boolean {
  if (mainCustCode === custCode) return false;
  const custCodes = customerPriceCodeVariants(custCode);
  if (custCodes.includes(priceCode)) return false;
  const main = cleanPriceCode(mainCustCode);
  const pcode = cleanPriceCode(priceCode);
  if (!pcode.startsWith(main + '-')) return false;
  const suffix = pcode.slice(main.length + 1);
  return /^S\d+$/i.test(suffix) || /^5\d+$/.test(suffix);
}

function canUseSiblingOutletFallback(
  mainCustCode: string,
  custCode: string,
  outletGroup: string,
  parentGroup: string,
): boolean {
  if (mainCustCode === custCode) return false;
  const outlet = cleanPriceCode(outletGroup).replace('NONE', '');
  const parent = cleanPriceCode(parentGroup).replace('NONE', '');
  if (!parent) return false;
  if (outlet && outlet !== parent) return false;
  return true;
}

function classifyPriceBucket(
  ptype: string,
  pcode: string,
  custCode: string,
  mainCustCode: string,
  custGroup: string,
  outletGroup: string,
  parentGroup: string,
): 'cust' | 'sibling' | 'main' | 'group' | 'all' | '' {
  const custCodes = customerPriceCodeVariants(custCode);
  const mainCodes = customerPriceCodeVariants(mainCustCode);

  if (custCodes.includes(pcode)) return 'cust';
  if (mainCustCode !== custCode && mainCodes.includes(pcode)) return 'main';
  if (custGroup && pcode === custGroup) return 'group';

  const isAllType = ptype === '2' || ptype === '4' || ptype === 'all' || ptype === 'all customers';
  if (!pcode || pcode === 'EMPTY' || isAllType) return 'all';

  if (
    mainCustCode !== custCode
    && isSiblingOutletPriceCode(mainCustCode, custCode, pcode)
    && canUseSiblingOutletFallback(mainCustCode, custCode, outletGroup, parentGroup)
  ) {
    return 'sibling';
  }

  return '';
}

export async function fetchPricesForItems(itemCodes: string[]): Promise<DemPriceRow[]> {
  const unique = [...new Set(itemCodes.map((c) => normalizeUpper(c)).filter(Boolean))];
  if (unique.length === 0) return [];

  const db = getDbSupabase('dem');
  const rows: DemPriceRow[] = [];
  const chunkSize = 40;

  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);
    const { data, error } = await db
      .from('prices')
      .select('item_code,price_type,price_code,start_date,end_date,uom,min_qty,price')
      .in('item_code', chunk);
    if (error) throw new Error(formatSupabaseError(error));

    for (const raw of data ?? []) {
      const row = raw as Record<string, unknown>;
      const price = Number(row.price ?? 0);
      if (!Number.isFinite(price) || price <= 0) continue;
      rows.push({
        item: normalizeUpper(String(row.item_code ?? '')),
        type: cleanPriceType(String(row.price_type ?? '')),
        code: cleanPriceCode(String(row.price_code ?? '')),
        start: parseDbDate(row.start_date),
        end: parseDbDate(row.end_date, true),
        uom: normalizeUpper(String(row.uom ?? '')).replace('NONE', ''),
        min_qty: Number(row.min_qty ?? 0) || 0,
        price,
      });
    }
  }

  return rows;
}

/** Fetch sales prices for specific customer price groups (e.g. CATERING, CATERING2). */
export async function fetchPricesForPriceGroups(priceGroups: string[]): Promise<DemPriceRow[]> {
  const groups = [...new Set(priceGroups.map((g) => cleanPriceCode(g)).filter(Boolean))];
  if (groups.length === 0) return [];

  const db = getDbSupabase('dem');
  const rows: DemPriceRow[] = [];
  const pageSize = 1000;
  let from = 0;

  while (true) {
    const { data, error } = await db
      .from('prices')
      .select('item_code,price_type,price_code,start_date,end_date,uom,min_qty,price')
      .in('price_code', groups)
      .order('item_code', { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) throw new Error(formatSupabaseError(error));

    for (const raw of data ?? []) {
      const row = raw as Record<string, unknown>;
      const price = Number(row.price ?? 0);
      if (!Number.isFinite(price) || price <= 0) continue;
      rows.push({
        item: normalizeUpper(String(row.item_code ?? '')),
        type: cleanPriceType(String(row.price_type ?? '')),
        code: cleanPriceCode(String(row.price_code ?? '')),
        start: parseDbDate(row.start_date),
        end: parseDbDate(row.end_date, true),
        uom: normalizeUpper(String(row.uom ?? '')).replace('NONE', ''),
        min_qty: Number(row.min_qty ?? 0) || 0,
        price,
      });
    }

    if ((data ?? []).length < pageSize) break;
    from += pageSize;
  }

  return rows;
}

/**
 * Price for a fixed customer price group only (no customer / all-customers fallback).
 * Matches DEM prices where price_code = priceGroup (Nav "Customer Price Group" rows).
 */
export function getPriceGroupPrice(
  prices: DemPriceRow[],
  params: {
    itemCode: string;
    priceGroup: string;
    qty?: number;
    uom?: string;
    deliveryDate?: string;
  },
): number {
  const targetItem = normalizeUpper(params.itemCode);
  const priceGroup = cleanPriceCode(params.priceGroup);
  if (!targetItem || !priceGroup) return 0;

  const uom = normalizeUpper(params.uom ?? '').replace('NONE', '');
  const qty = Number(params.qty) || 1;
  const deliveryDate = parseDeliveryDateToLocalDate(params.deliveryDate ?? '');

  const valid: number[] = [];
  for (const p of prices) {
    if (p.item !== targetItem) continue;
    if (cleanPriceCode(p.code) !== priceGroup) continue;

    const rawPrice = p.price;
    if (rawPrice <= 0) continue;

    const pUom = p.uom === 'NONE' ? '' : p.uom;
    if (pUom && uom && pUom !== uom) continue;

    if (deliveryDate < p.start) continue;
    if (p.end.getTime() > 0 && deliveryDate > p.end) continue;
    if (qty < p.min_qty) continue;

    valid.push(rawPrice);
  }

  return minPrice(valid) ?? 0;
}

export type PriceGroupMoqTier = {
  minQty: number;
  price: number;
};

/**
 * All valid MOQ breaks for a price group (today's date, item UOM).
 * One row per min_qty; if multiple rows share min_qty, keeps the lowest price.
 */
export function getPriceGroupMoqTiers(
  prices: DemPriceRow[],
  params: {
    itemCode: string;
    priceGroup: string;
    uom?: string;
    deliveryDate?: string;
  },
): PriceGroupMoqTier[] {
  const targetItem = normalizeUpper(params.itemCode);
  const priceGroup = cleanPriceCode(params.priceGroup);
  if (!targetItem || !priceGroup) return [];

  const uom = normalizeUpper(params.uom ?? '').replace('NONE', '');
  const deliveryDate = parseDeliveryDateToLocalDate(params.deliveryDate ?? '');

  const byMoq = new Map<number, number>();

  for (const p of prices) {
    if (p.item !== targetItem) continue;
    if (cleanPriceCode(p.code) !== priceGroup) continue;
    if (p.price <= 0) continue;

    const pUom = p.uom === 'NONE' ? '' : p.uom;
    if (pUom && uom && pUom !== uom) continue;

    if (deliveryDate < p.start) continue;
    if (p.end.getTime() > 0 && deliveryDate > p.end) continue;

    const moq = Number.isFinite(p.min_qty) && p.min_qty > 0 ? p.min_qty : 0;
    const existing = byMoq.get(moq);
    if (existing == null || p.price < existing) {
      byMoq.set(moq, p.price);
    }
  }

  return [...byMoq.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([minQty, price]) => ({ minQty, price }));
}

/**
 * Navision sales price hierarchy (aligned with desktop DataManager.get_navision_price).
 * Customer -> parent customer -> price group -> all customers -> item base -> any item price.
 */
export function getNavisionPrice(
  prices: DemPriceRow[],
  params: {
    itemCode: string;
    custCode: string;
    qty: number;
    uom: string;
    deliveryDate: string;
    customers: DemCustomer[];
    itemBasePrice?: number;
  },
): number {
  const targetItem = normalizeUpper(params.itemCode);
  if (!targetItem) return 0;

  const custCode = normalizeUpper(params.custCode);
  const uom = normalizeUpper(params.uom).replace('NONE', '');
  const qty = Number(params.qty) || 0;
  const deliveryDate = parseDeliveryDateToLocalDate(params.deliveryDate);

  const mainCustCode = custCode.includes('-') ? custCode.split('-')[0]!.trim() : custCode;
  const customer = params.customers.find((c) => normalizeUpper(c.code) === custCode);
  const parent =
    mainCustCode !== custCode
      ? params.customers.find((c) => normalizeUpper(c.code) === mainCustCode)
      : undefined;

  const outletGroup = normalizeUpper(customer?.price_group ?? '').replace('NONE', '');
  const parentGroup = parent ? normalizeUpper(parent.price_group ?? '').replace('NONE', '') : '';
  const custGroup = outletGroup || parentGroup;

  const validCust: number[] = [];
  const validSibling: number[] = [];
  const validMainCust: number[] = [];
  const validGroup: number[] = [];
  const validAll: number[] = [];
  const itemAny: number[] = [];

  for (const p of prices) {
    if (p.item !== targetItem) continue;

    const rawPrice = p.price;
    if (rawPrice <= 0) continue;

    const pUom = p.uom === 'NONE' ? '' : p.uom;
    if (pUom && uom && pUom !== uom) continue;

    if (deliveryDate < p.start) continue;
    if (p.end.getTime() > 0 && deliveryDate > p.end) continue;
    if (qty < p.min_qty) continue;

    itemAny.push(rawPrice);

    let ptype = p.type === 'none' ? '' : p.type;
    let pcode = p.code === 'NONE' ? '' : p.code;
    if (ptype === 'none') ptype = '';
    if (pcode === 'NONE') pcode = '';

    const bucket = classifyPriceBucket(
      ptype,
      pcode,
      custCode,
      mainCustCode,
      custGroup,
      outletGroup,
      parentGroup,
    );
    if (bucket === 'cust') validCust.push(rawPrice);
    else if (bucket === 'sibling') validSibling.push(rawPrice);
    else if (bucket === 'main') validMainCust.push(rawPrice);
    else if (bucket === 'group') validGroup.push(rawPrice);
    else if (bucket === 'all') validAll.push(rawPrice);
  }

  const custPrice = minPrice(validCust);
  if (custPrice != null) return custPrice;

  const siblingPrice = minPrice(validSibling);
  if (siblingPrice != null) return siblingPrice;

  const mainCustPrice = minPrice(validMainCust);
  if (mainCustPrice != null) return mainCustPrice;

  const groupPrice = minPrice(validGroup);
  if (groupPrice != null) return groupPrice;

  const allPrice = minPrice(validAll);
  if (allPrice != null) return allPrice;

  const base = Number(params.itemBasePrice ?? 0);
  if (Number.isFinite(base) && base > 0) return base;

  const anyPrice = minPrice(itemAny);
  if (anyPrice != null) return anyPrice;

  return 0;
}

export function formatSysPrice(value: number): string {
  return value.toFixed(3);
}

function parseFactor(value: string): number {
  const n = Number(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function computeRowSysPrice(
  prices: DemPriceRow[],
  row: DemPoReviewRow,
  header: DemPoExtractHeader,
  customers: DemCustomer[],
  itemBasePrice = 0,
): string {
  const code = row.sysCode.trim().toUpperCase();
  if (!code || ['IGNORE', 'SKIP', 'N/A', 'NONE'].includes(code)) return '0.000';

  const base = getNavisionPrice(prices, {
    itemCode: row.sysCode,
    custCode: header.sys_customer_code ?? '',
    qty: row.finalQty,
    uom: row.sysUom || row.pdfUom,
    deliveryDate: header.delivery_date,
    customers,
    itemBasePrice,
  });

  return formatSysPrice(base * parseFactor(row.factor));
}

export function applySysPricesToRows(
  rows: DemPoReviewRow[],
  prices: DemPriceRow[],
  header: DemPoExtractHeader,
  customers: DemCustomer[],
  itemBasePrices: Record<string, number> = {},
): DemPoReviewRow[] {
  return rows.map((row) => ({
    ...row,
    sysPrice: computeRowSysPrice(
      prices,
      row,
      header,
      customers,
      itemBasePrices[row.sysCode.trim().toUpperCase()] ?? 0,
    ),
  }));
}
